/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Button, Typography } from '@equinor/eds-core-react';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import {
  AnalogueModelDetail,
  putApiV1AnalogueModelsById,
  UpdateAnalogueModelCommandBody,
} from '../../../api/generated';
import { GrossDepositionEnviromentGroup } from '../../../components/GrossDepositionEnviroment/GrossDepositionEnviromentGroup/GrossDepositionEnviromentGroup';
import { OutcropAnalogueGroup } from '../../../components/OutcropAnalogue/OutcropAnalogueGroup/OutcropAnalogueGroup';
import { StratigrapicGroups } from '../../../components/StrategraphicColumn/StratigrapicGroups/StratigrapicGroups';
import { EditNameDescription } from '../EditNameDescription/EditNameDescription';
import * as Styled from '../ModelMetadataView/ModelMetadataView.styled';
import { useIsOwnerOrAdmin } from '../../../hooks/useIsOwnerOrAdmin';
import { useModelFormStore } from '../../../stores/ModelFormStore';
import { analogueModelDefault } from '../../../constants/analogueModelDefault';
import { useShallow } from 'zustand/react/shallow';
import { useFetchOutcropData } from '../../../hooks/useFetchOutcropData';
import {
  useFetchSmdaCountries,
  useFetchSmdaFields,
  useFetchSmdaMetadataStratigraphicUnits,
  useFetchSmdaStratigraphicColumns,
} from '../../../hooks/useFetchStratColData';
import { useFetchGrossDepData } from '../../../hooks/useFetchGrossDepData';
import { IniParametersDialog } from '../IniParametersDialog/IniParametersDialog';
import { IniParametersWrapper } from '../ModelMetadataView/ModelMetadataView.styled';
import { ModelStatus } from '../../ModelTable/ModelTable';
import { UploadingStatus } from '../../../pages/AddModel/stores/AddModelStore';

export const ModelAddView = ({
  uploadingStatus,
}: {
  uploadingStatus?: UploadingStatus;
}) => {
  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const { analogueModel, analogueModelImageURL, setAnalogueModel } =
    useModelFormStore(
      useShallow((s) => ({
        analogueModel: s.analogueModel,
        analogueModelImageURL: s.analogueModelImageURL,
        setAnalogueModel: s.setAnalogueModel,
      })),
    );

  // Prefetch reference data so child components share the RQ cache
  useFetchOutcropData();
  useFetchSmdaCountries();
  useFetchSmdaFields();
  useFetchSmdaStratigraphicColumns();
  useFetchSmdaMetadataStratigraphicUnits();
  useFetchGrossDepData();

  const [isAddModelDialog, setAddModelDialog] = useState<boolean>(false);

  const generateImageRequested = useRef(false);

  function toggleEditMetadata() {
    setAddModelDialog(!isAddModelDialog);
  }

  const updateNameDescription = useMutation({
    mutationKey: ['analogue-model'],
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: UpdateAnalogueModelCommandBody;
    }) => {
      return putApiV1AnalogueModelsById({
        body: requestBody,
        path: { id: id },
      });
    },
  });

  const updateModelMetadata = async (metadata: AnalogueModelDetail) => {
    const id = analogueModel.analogueModelId
      ? analogueModel.analogueModelId
      : '';
    const modelMetadata = {
      name: metadata.name,
      description: metadata.description,
      sourceType: 'Deltares',
    };

    await updateNameDescription.mutateAsync({
      id: id,
      requestBody: modelMetadata,
    });
    setAnalogueModel(metadata);
    toggleEditMetadata();
  };

  const getModelStatus = () => {
    let status = ModelStatus.UNKNOWN;

    const transforming =
      analogueModel && analogueModel.processingStatus
        ? analogueModel.processingStatus
        : undefined;
    const isProcessed = analogueModel ? analogueModel.isProcessed : undefined;

    if (isProcessed === true) {
      status = ModelStatus.SUCCEEDED;
    } else if (
      transforming === 'Created' ||
      transforming === 'Waiting' ||
      transforming === 'Running'
    ) {
      status = ModelStatus.TRANSFORMING;
    } else if (transforming === 'Failed') {
      status = ModelStatus.FAILED_TRANSFORMING;
    } else if (isProcessed === false) {
      status = ModelStatus.FAILED_UPLOADING;
    }

    return status;
  };

  if (analogueModel === analogueModelDefault && uploadingStatus === undefined)
    return <p>Loading ...</p>;

  return (
    <>
      {uploadingStatus === undefined && (
        <Styled.DescriptionImageWrapper>
          <Styled.DescriptionMeta>
            <Typography variant="h3">Description</Typography>
            <>
              {analogueModel.description && (
                <Typography variant="body_long">
                  {analogueModel.description}
                </Typography>
              )}
            </>

            {isOwnerOrAdmin && (
              <Button
                onClick={toggleEditMetadata}
                variant="outlined"
                className="edit-metadata-button"
              >
                Edit name and description…
              </Button>
            )}
            <EditNameDescription
              edit={updateModelMetadata}
              isEdit={isAddModelDialog}
              closeDialog={toggleEditMetadata}
              modelId={analogueModel.analogueModelId}
            />
          </Styled.DescriptionMeta>
          {analogueModelImageURL && analogueModel !== analogueModelDefault && (
            <Styled.ModelImageView>
              <img src={analogueModelImageURL} alt=""></img>
              <Typography>{analogueModel.name}</Typography>
            </Styled.ModelImageView>
          )}
          <Styled.ImageMessage>
            {analogueModel.isProcessed &&
              !analogueModelImageURL &&
              generateImageRequested.current && (
                <div>
                  <Typography as="p">
                    We are generating image for this analogue model
                  </Typography>
                  <Typography as="p">
                    Please come back to this page in a couple of minutes
                  </Typography>
                </div>
              )}
            {getModelStatus() === ModelStatus.TRANSFORMING && (
              <div>
                <Typography as="p">
                  Model picture will be generated after the model is
                  transformed.
                </Typography>
              </div>
            )}
            {getModelStatus() === ModelStatus.FAILED_TRANSFORMING && (
              <div>
                <Typography as="p">
                  Model transformation failed. Delete the model and upload
                  again.
                </Typography>
              </div>
            )}
            {!analogueModel.processingStatus && (
              <div>
                <Typography as="p">
                  Cannot generate picture for unprocessed model.
                </Typography>
                <Typography as="p">
                  If processing failed, delete this model and reupload again.
                  Else, wait.
                </Typography>
              </div>
            )}
            {!analogueModel.isProcessed && (
              <div>
                <Typography as="p">
                  Cannot generate picture for unprocessed model.
                </Typography>
                <Typography as="p">
                  If processing failed, delete this model and reupload again.
                  Else, wait.
                </Typography>
              </div>
            )}
          </Styled.ImageMessage>
        </Styled.DescriptionImageWrapper>
      )}
      {uploadingStatus === UploadingStatus.Uploading && (
        <Styled.UploadingMeta>
          <Typography variant="body_long">
            While your model is being uploaded, you can add some metadata like
            outcrop analogue, stratigraphic column, or gross depositional
            environment. Please note that at least one type of metadata is
            required for PEPM models before they can be approved later on.
          </Typography>
        </Styled.UploadingMeta>
      )}

      <Typography variant="h3" as="h2">
        Model metadata
      </Typography>
      {analogueModel.iniParameters && uploadingStatus === undefined && (
        <IniParametersWrapper>
          <Typography variant="h4" as="h3">
            Ini parameters
          </Typography>
          <div>
            <IniParametersDialog iniParameters={analogueModel.iniParameters} />
          </div>
        </IniParametersWrapper>
      )}
      <div>
        <OutcropAnalogueGroup modelId={analogueModel.analogueModelId} />
      </div>
      <div>
        <StratigrapicGroups modelId={analogueModel.analogueModelId} />
      </div>
      <div>
        <GrossDepositionEnviromentGroup
          modelId={analogueModel.analogueModelId}
        />
      </div>
    </>
  );
};
